import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

interface PageContent {
  section_key: string;
  content: any;
}

const AdminPanel: React.FC = () => {
  const [content, setContent] = useState<PageContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [activeSection, setActiveSection] = useState<string>('hero');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data, error } = await supabase
        .from('page_content')
        .select('*')
        .order('section_key');

      if (error) throw error;
      setContent(data || []);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (sectionKey: string, field: string, value: any) => {
    setContent(prev => prev.map(section => {
      if (section.section_key === sectionKey) {
        return {
          ...section,
          content: {
            ...section.content,
            [field]: value
          }
        };
      }
      return section;
    }));
  };

  const handleArrayChange = (sectionKey: string, field: string, index: number, value: string) => {
    setContent(prev => prev.map(section => {
      if (section.section_key === sectionKey) {
        const newArray = [...section.content[field]];
        newArray[index] = value;
        return {
          ...section,
          content: {
            ...section.content,
            [field]: newArray
          }
        };
      }
      return section;
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      for (const section of content) {
        const { error } = await supabase
          .from('page_content')
          .update({ content: section.content, updated_at: new Date().toISOString() })
          .eq('section_key', section.section_key);

        if (error) throw error;
      }

      setMessage('Content saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving content:', error);
      setMessage('Error saving content. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <p className="text-white text-xl">Loading...</p>
      </div>
    );
  }

  const activeContent = content.find(c => c.section_key === activeSection);

  return (
    <div className="min-h-screen bg-navy">
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-light text-navy">Admin Panel</h1>
          <div className="flex gap-4">
            <a
              href="/"
              className="px-4 py-2 border border-navy text-navy rounded-lg hover:bg-navy hover:text-white transition-colors"
            >
              View Site
            </a>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-opacity-90 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8" style={{ marginTop: '2rem' }}>
        <div className="flex gap-8">
          <div style={{ width: '30%', minWidth: '250px' }}>
            <div className="bg-white rounded-lg shadow-card p-4">
              <h2 className="text-lg font-medium text-navy mb-4">Sections</h2>
              <div className="space-y-2">
                {content.map(section => (
                  <button
                    key={section.section_key}
                    onClick={() => setActiveSection(section.section_key)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                      activeSection === section.section_key
                        ? 'bg-navy text-white'
                        : 'bg-gray-100 text-navy hover:bg-gray-200'
                    }`}
                    style={{ textAlign: 'left' }}
                  >
                    {section.section_key.charAt(0).toUpperCase() + section.section_key.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div className="bg-white rounded-lg shadow-card p-6">
              {activeContent && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-light text-navy mb-6">
                    Edit {activeContent.section_key.charAt(0).toUpperCase() + activeContent.section_key.slice(1)} Section
                  </h2>

                  {Object.entries(activeContent.content).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-navy font-medium mb-2 capitalize">
                        {key.replace(/_/g, ' ')}
                      </label>
                      
                      {Array.isArray(value) ? (
                        <div className="space-y-3">
                          {value.map((item: string, index: number) => (
                            <textarea
                              key={index}
                              value={item}
                              onChange={(e) => handleArrayChange(activeContent.section_key, key, index, e.target.value)}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent resize-vertical"
                              rows={2}
                              style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', overflowWrap: 'break-word' }}
                            />
                          ))}
                        </div>
                      ) : (
                        <textarea
                          value={value as string}
                          onChange={(e) => handleContentChange(activeContent.section_key, key, e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent resize-vertical"
                          rows={typeof value === 'string' && value.length > 100 ? 4 : 2}
                          style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', overflowWrap: 'break-word' }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {message && (
                <div className={`mt-6 p-4 rounded-lg ${
                  message.includes('Error') 
                    ? 'bg-red-50 border border-red-200 text-red-800' 
                    : 'bg-green-50 border border-green-200 text-green-800'
                }`}>
                  {message}
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-3 bg-navy text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save All Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
